// Copyright © 2021 Move Closer

import { AdaptsObjectToToken, AdaptsTokenToObject, HTMLToken, TokenType } from '@d24/demarked'

import { AbstractAdapter } from '../_abstract/adapter'
import { ModuleDriver } from '../_contracts'
import { getModuleBase } from '../_helpers/adapter'

import { HTMLModule } from './HTML.contracts'

/**
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 */
export class HTMLAdapter extends AbstractAdapter implements
  AdaptsObjectToToken<ModuleDriver.HTML, HTMLModule, HTMLToken>,
  AdaptsTokenToObject<HTMLToken, HTMLModule> {
  /**
   * Adapts (translates/maps) the passed-in `HTMLModule` to the `HTMLToken`.
   *
   * @param object - The module to adapt.
   */
  public objectToToken (object: HTMLModule): HTMLToken {
    this.validate(object, {
      'content.html': {
        presence: true,
        type: 'string'
      }
    })

    if (typeof object.content.scripts !== 'undefined') {
      this.validate(object, {
        'content.scripts': {
          type: 'array'
        }
      })

      object.content.scripts.forEach(script => {
        this.validate(script, {
          src: {
            presence: true,
            type: 'string'
          }
        })
      })
    }

    const token: HTMLToken = {
      content: object.content.html,
      raw: object.content.html,
      type: TokenType.HTML
    }

    const scripts = object.content.scripts
    if (typeof scripts !== 'undefined' && Array.isArray(scripts) && scripts.length > 0) {
      token.meta = { scripts }

      scripts.forEach(script => {
        const attrs: string = Object.entries(script).reduce<string>(
          (acc, [key, value]) => {
            return `${acc} ${key}${typeof value !== 'undefined' ? `="${value}"` : ''}`
          }, '')

        token.raw += `<script${attrs}></script>`
      })
    }

    return token
  }

  /**
   * Adapts (translates/maps) the passed-in `HTMLToken` to the `HTMLModule`.
   *
   * @param token - The token to adapt.
   */
  public tokenToObject (token: HTMLToken): HTMLModule {
    this.validate(token, {
      content: {
        presence: true,
        type: 'string'
      }
    })

    if (typeof token.meta !== 'undefined' && typeof token.meta.scripts !== 'undefined') {
      this.validate(token.meta, {
        scripts: {
          type: 'array'
        }
      })

      token.meta.scripts.forEach(script => {
        this.validate(script, {
          src: {
            presence: true,
            type: 'string'
          }
        })
      })
    }

    const module: HTMLModule = {
      ...getModuleBase(),
      content: {
        html: token.content
      },
      driver: ModuleDriver.HTML
    }

    if (typeof token.meta !== 'undefined' && typeof token.meta.scripts !== 'undefined') {
      module.content.scripts = token.meta.scripts
    }

    return module
  }
}
